
import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { updateGcliAmount } from "../../features/actuarial/rates-slice";
import { getAmountKind, getMatchingItem, getRateAcronymFor6680, hasValidNumericFormat } from "../../helper/rates/rates-input-helper";
import { GcliRateGroup, TypeOfAgeRates } from "../../features/actuarial/rates-state";
import { RatesUpdateType } from "../../types/type";

export type StartAge = "66" | "71" | "76";
export type EndAge = "70" | "75" | "80";
export type AgeBracketType = "66-70" | "71-75" | "76-80";

interface Props {
    rates: GcliRateGroup[];
    typeOfAgeRates: TypeOfAgeRates;
    typeOfRates: boolean;
    setAmountWarning: (value: string | null) => void;
    startAge: StartAge;
    endAge: EndAge;
    ageBracket: AgeBracketType;
}

export const useRateAmountChange = ({ rates, typeOfAgeRates, typeOfRates, setAmountWarning, startAge, endAge, ageBracket }: Props) => {
    const dispatch = useDispatch();
    const previousInputValues = useRef<Record<string, string>>({});

    const handleChangeAmount = useCallback((id: number, value: string, age: string, type: RatesUpdateType) => {
        // Other products: existing validation remains unchanged.
        if (!typeOfRates) {
            const currentItem = getMatchingItem(age, id, type, rates);
            const monthName = currentItem?.name || getRateAcronymFor6680(id, type, rates);
            const isClearing = value.trim() === "";
            const ageNumber = Number(age);
            const normalizedValue = value.trim().toUpperCase();

            const otherAmounts = rates
                .filter((group) => group.title !== age)
                .map((group) => ({
                    age: group.title,
                    amount: getMatchingItem(group.title, id, type, rates)?.amount ?? "",
                }))
                .filter((item) => item.amount.trim() !== "");

            const currentAmount = currentItem?.amount?.trim().toUpperCase() ?? "";
            const isAutoPopulatedTextValue = ["N/A"].includes(currentAmount);

            // Allow clearing N/A across all ages when clearing the starting age.
            if (age === startAge && isClearing && isAutoPopulatedTextValue) {
                rates
                    .filter((group) => Number(group.title) >= Number(startAge) && Number(group.title) <= Number(endAge))
                    .forEach((group) => {
                        const matchingItem = group.data.find((item) => {
                            if (item.type !== type) return false;
                            return type === "plan" ? item.plan_id === id : item.rider_id === id;
                        });

                        if (!matchingItem) return;

                        dispatch(updateGcliAmount({ group: typeOfAgeRates, title: group.title, id, amount: "", type }));
                    });

                setAmountWarning(null);
                return;
            }

            // Preserve the existing restriction for other values.
            if (age === startAge && isClearing && otherAmounts.some((item) => Number(item.age) > Number(startAge))) {
                setAmountWarning(`You cannot clear ${monthName} at age ${Number(startAge)} while the same loan term has amounts for other ages. Clear those amounts first.`);
                return;
            }

            // Require the immediately preceding age to be populated.
            if (ageNumber > Number(startAge) && !isClearing) {
                const previousAge = String(ageNumber - 1);
                const previousAmount = getMatchingItem(previousAge, id, type, rates)?.amount?.trim() ?? "";

                if (!previousAmount) {
                    setAmountWarning(`Enter ${monthName} at age ${previousAge} first before entering an amount for age ${age}.`);
                    return;
                }
            }

            const newKind = getAmountKind(value);

            // Numeric values can have a maximum of 4 decimal places.
            if (newKind === "number" && !isClearing && !hasValidNumericFormat(value)) {
                setAmountWarning("Only numeric values with a maximum of 4 decimal places or N/A are allowed.");
                return;
            }

            // Silently reject extra characters after a completed N/A value.
            const appendedToCompletedValue = ["N/A"].some((allowed) => currentAmount === allowed && normalizedValue.startsWith(allowed) && normalizedValue.length > allowed.length);

            if (appendedToCompletedValue) {
                return;
            }

            // Only allow numeric values or N/A.
            if (newKind === "text") {
                const isAllowedPrefix = "N/A".startsWith(normalizedValue);

                if (!isAllowedPrefix) {
                    setAmountWarning("Only numeric values or N/A are allowed.");
                    return;
                }

                // Show a warning when another age already contains a numeric value.
                const numericConflict = otherAmounts.find((item) => getAmountKind(item.amount) === "number");

                if (numericConflict) {
                    setAmountWarning(`${monthName} must remain numeric across ages ${ageBracket} because age ${numericConflict.age} contains a numeric value.`);
                    return;
                }
            }

            // A rider must follow the plan value at the same age.
            if (type === "rider" && !isClearing) {
                const currentAgeGroup = rates.find((group) => group.title === age);
                const planItem = currentAgeGroup?.data.find((item) => item.type === "plan");
                const planAmount = planItem?.amount?.trim() ?? "";
                const planKind = getAmountKind(planAmount);

                if (planAmount && planKind === "text") {
                    const normalizedPlanAmount = planAmount.toUpperCase();

                    if (!normalizedPlanAmount.startsWith(normalizedValue)) {
                        setAmountWarning(`The rider amount must match the plan value "${planAmount}".`);
                        return;
                    }
                } else if (planAmount && planKind === "number") {
                    if (newKind !== "number") {
                        setAmountWarning(`The rider amount must be numeric because the product amount is ${planAmount}.`);
                        return;
                    }
                }
            }

            // Keep the same value type across ages for this plan or rider.
            if (newKind !== null) {
                const inconsistentAmount = otherAmounts.find((item) => getAmountKind(item.amount) !== newKind);

                if (inconsistentAmount) {
                    const existingKind = getAmountKind(inconsistentAmount.amount);
                    setAmountWarning(`${monthName} must remain ${existingKind === "number" ? "numeric" : "characters"} across ages ${ageBracket}.`);
                    return;
                }
            }

            // Keep text values consistent across populated ages.
            if (newKind === "text" && !isClearing) {
                const partialConflict = otherAmounts.find((item) => {
                    if (getAmountKind(item.amount) !== "text") return false;
                    const existingValue = item.amount.trim().toUpperCase();
                    return !(existingValue.startsWith(normalizedValue) || normalizedValue.startsWith(existingValue));
                });

                if (partialConflict) {
                    setAmountWarning(`${monthName} must match the value used across ages ${ageBracket}. Existing value: "${partialConflict.amount.trim()}".`);
                    return;
                }
            }

            setAmountWarning(null);

            const acceptedValue = value.trim().toUpperCase();

            dispatch(updateGcliAmount({ group: typeOfAgeRates, title: age, id, amount: acceptedValue, type }));

            // Automatically populate subsequent ages only for N/A.
            if (["N/A"].includes(acceptedValue)) {
                rates
                    .filter((group) => Number(group.title) > ageNumber && Number(group.title) <= Number(endAge))
                    .forEach((group) => {
                        const matchingItem = group.data.find((item) => {
                            if (item.type !== type) return false;
                            return type === "plan" ? item.plan_id === id : item.rider_id === id;
                        });

                        if (!matchingItem) return;

                        dispatch(updateGcliAmount({ group: typeOfAgeRates, title: group.title, id, amount: acceptedValue, type }));
                    });
            }

            return;
        }

        // GYRT/GPA validation.
        const acronym = getRateAcronymFor6680(id, type, rates);
        const currentAgeIndex = rates.findIndex((group) => group.title === age);
        const currentAgeGroup = rates[currentAgeIndex];
        const planItem = currentAgeGroup?.data.find((item) => item.type === "plan");
        const normalizedValue = value.trim().toUpperCase();
        const isClearing = value.trim() === "";
        const newKind = getAmountKind(value);
        const isNumeric = hasValidNumericFormat(value);
        const isAllowedRiderText = ["FREE", "N/A"].includes(normalizedValue);
        const fieldKey = `${age}-${type}-${id}`;

        const previousValue = (
            previousInputValues.current[fieldKey] ?? (
                type === "plan"
                    ? planItem?.amount ?? ""
                    : currentAgeGroup?.data.find((item) => item.type === "rider" && item.rider_id === id)?.amount ?? ""
            )
        ).trim().toUpperCase();

        // Prevent additional characters after a completed FREE or N/A Rider value.
        if (type === "rider" && ["FREE", "N/A"].includes(previousValue) && !isClearing) {
            return;
        }

        // Riders accept numeric values, FREE, or N/A.
        // Partial FREE/N/A input is allowed while typing.
        if (type === "rider" && !isClearing) {
            const isPartialAllowedText = ["FREE", "N/A"].some((allowed) => allowed.startsWith(normalizedValue));

            const isValidRiderInput =
                (newKind === "number" && isNumeric) ||
                isAllowedRiderText ||
                (isPartialAllowedText && getAmountKind(value) === "text");

            if (!isValidRiderInput) {
                setAmountWarning("Rider amount must be numeric with a maximum of 4 decimal places, FREE, or N/A.");
                return;
            }
        }

        // Numeric values can have a maximum of 4 decimal places.
        if (newKind === "number" && !isClearing && !isNumeric) {
            setAmountWarning("Numeric values can have a maximum of 4 decimal places.");
            return;
        }

        // Clear an auto-populated FREE/N/A Rider across the selected age bracket.
        const isReplacingLockedText =
            type === "rider" &&
            ["FREE", "N/A"].includes(previousValue) &&
            isClearing;

        if (isReplacingLockedText) {
            rates
                .filter((group) => Number(group.title) >= Number(startAge) && Number(group.title) <= Number(endAge))
                .forEach((group) => {
                    const matchingRider = group.data.find((item) => item.type === "rider" && item.rider_id === id);

                    if (!matchingRider) return;

                    dispatch(updateGcliAmount({ group: typeOfAgeRates, title: group.title, id, amount: "", type: "rider" }));
                    previousInputValues.current[`${group.title}-rider-${id}`] = "";
                });

            setAmountWarning(null);
            return;
        }

        // Plans accept numeric values only, up to 4 decimal places.
        // if (type === "plan" && !isClearing) {
        //     if (newKind !== "number" || !isNumeric) {
        //         setAmountWarning(`${acronym} product amount must be numeric with a maximum of 4 decimal places.`);
        //         return;
        //     }

        //     const startAgePlan = getMatchingItem(startAge, id, "plan", rates);
        //     const startAgePlanAmount = startAgePlan?.amount?.trim() ?? "";

        //     if (
        //         Number(age) > Number(startAge) &&
        //         startAgePlanAmount &&
        //         (getAmountKind(startAgePlanAmount) !== "number" || !hasValidNumericFormat(startAgePlanAmount))
        //     ) {
        //         setAmountWarning(`The product amount must be numeric because the amount at age ${startAge} is not numeric.`);
        //         return;
        //     }
        // }
        
        // Plans accept numeric values with up to 4 decimal places or N/A.
        // Allow partial N/A input while the user is typing.
        if (type === "plan" && !isClearing) {
            const currentPlanAmount = planItem?.amount?.trim().toUpperCase() ?? "";
            // Prevent additional characters after N/A is complete.
            if (currentPlanAmount === "N/A" && newKind === "text") {
                return;
            }
            const isValidPlanInput =
                (newKind === "number" && isNumeric) ||
                (newKind === "text" && "N/A".startsWith(normalizedValue));

            if (!isValidPlanInput) {
                setAmountWarning(
                    `${acronym} product amount must be numeric with a maximum of 4 decimal places or N/A.`
                );
                return;
            }

            const startAgePlan = getMatchingItem(startAge, id, "plan", rates);
            const startAgePlanAmount = startAgePlan?.amount?.trim() ?? "";

            if (
                Number(age) > Number(startAge) &&
                startAgePlanAmount &&
                (
                    getAmountKind(startAgePlanAmount) !== "number" ||
                    !hasValidNumericFormat(startAgePlanAmount)
                ) &&
                startAgePlanAmount.toUpperCase() !== "N/A"
            ) {
                setAmountWarning(
                    `The product amount must be numeric or N/A because the amount at age ${startAge} is not numeric.`
                );
                return;
            }
        }


        // Validate Rider values against previous ages.
        // FREE and N/A may be entered at any age and propagated across the bracket.
        if (type === "rider" && !isClearing && currentAgeIndex !== -1 && !isAllowedRiderText) {
            for (let ageIndex = 0; ageIndex < currentAgeIndex; ageIndex++) {
                const previousGroup = rates[ageIndex];

                const previousRider = previousGroup.data.find(
                    (item) => item.type === "rider" && item.rider_id === id
                );

                const previousAmount = previousRider?.amount?.trim().toUpperCase() ?? "";

                if (!previousAmount) continue;

                const previousKind = getAmountKind(previousAmount);

                if (previousKind === "number" && newKind !== "number") {
                    setAmountWarning(`Rider ${acronym} at age ${age} must be numeric because it is numeric at age ${previousGroup.title}.`);
                    return;
                }

                if (["FREE", "N/A"].includes(previousAmount) && normalizedValue !== previousAmount) {
                    setAmountWarning(`Rider ${acronym} at age ${age} must match the ${previousAmount} value at age ${previousGroup.title}. Clear the existing value first.`);
                    return;
                }
            }
        }

        // Validate existing Riders when changing a Plan.
        // if (type === "plan" && currentAgeGroup && !isClearing) {
        //     const riders = currentAgeGroup.data.filter((item) => item.type === "rider" && item.amount.trim() !== "");

        //     if (newKind === "number") {
        //         const hasTextRider = riders.some((item) => getAmountKind(item.amount) === "text");

        //         if (hasTextRider) {
        //             setAmountWarning(`All rider amounts at age ${age} must be numeric when the product amount is numeric.`);
        //             return;
        //         }
        //     }
        // }

        // Plans accept numeric values only, with a maximum of 4 decimal places.
        // if (type === "plan" && !isClearing) {
        //     if (newKind !== "number" || !isNumeric) {
        //         setAmountWarning(
        //             `${acronym} product amount must be numeric with a maximum of 4 decimal places.`
        //         );
        //         return;
        //     }
        // }

        setAmountWarning(null);

        const acceptedValue = normalizedValue;

        dispatch(updateGcliAmount({ group: typeOfAgeRates, title: age, id, amount: acceptedValue, type }));
        previousInputValues.current[fieldKey] = acceptedValue;

        // Automatically populate the same Rider across the entire selected age bracket.
        if (type === "rider" && ["FREE", "N/A"].includes(acceptedValue)) {
            rates
                .filter((group) => Number(group.title) >= Number(startAge) && Number(group.title) <= Number(endAge))
                .forEach((group) => {
                    const matchingRider = group.data.find((item) => item.type === "rider" && item.rider_id === id);

                    if (!matchingRider) return;

                    dispatch(updateGcliAmount({ group: typeOfAgeRates, title: group.title, id, amount: acceptedValue, type: "rider" }));
                    previousInputValues.current[`${group.title}-rider-${id}`] = acceptedValue;
                });
        }
    }, [dispatch, rates, typeOfAgeRates, typeOfRates, setAmountWarning, startAge, endAge, ageBracket]);

    return { handleChangeAmount };
};
